import { useMemo, useRef, useState } from "react";
import { BarChart3, ChevronDown, Play, Presentation, Users } from "lucide-react";
import { Button, Card, EmptyState, PageHeader } from "@/components/ui-ext/primitives";
import { ArrangeDeckModal } from "@/components/engineering-insights/ArrangeDeckModal";
import { DateRangePopover } from "@/components/engineering-insights/DateRangePopover";
import { EngineeringChart } from "@/components/engineering-insights/EngineeringChart";
import {
  InsightsChatSidebar,
  type InsightsChatSidebarHandle,
} from "@/components/engineering-insights/InsightsChatSidebar";
import { MeetingModeOverlay } from "@/components/engineering-insights/MeetingModeOverlay";
import {
  useEngineeringInsightsStatus,
  useEngineeringPresentationSettings,
  type EngineeringInsightsGranularity,
} from "@/lib/engineering-insights/api";
import { type SquadMode } from "@/lib/engineering-insights/chart-data";
import {
  autoGranularityForRange,
  rangeForPreset,
  type RangePreset,
} from "@/lib/engineering-insights/date-range";
import {
  ENGINEERING_CHART_SECTIONS,
  ENGINEERING_CHART_SPECS,
} from "@/lib/engineering-insights/chart-specs";
import {
  resolveArrangement,
  resolveDeck,
} from "@/lib/engineering-insights/presentation";
import { cn } from "@/lib/utils";

const RANGE_PRESETS: Array<{ id: Exclude<RangePreset, "custom">; label: string }> = [
  { id: "1m", label: "Last month" },
  { id: "3m", label: "Last 3 months" },
  { id: "6m", label: "Last 6 months" },
  { id: "12m", label: "Last 12 months" },
  { id: "ytd", label: "Year to date" },
];

const SQUAD_MODE_OPTIONS: Array<{ value: SquadMode; label: string }> = [
  { value: "all", label: "All squads" },
  { value: "compare", label: "Member vs B2B" },
  { value: "member", label: "Member" },
  { value: "b2b", label: "B2B" },
];

const GRANULARITY_OPTIONS = [
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
] as const;

interface RangeState {
  preset: RangePreset;
  from: string;
  to: string;
}

export function EngineeringInsightsPage() {
  const chatRef = useRef<InsightsChatSidebarHandle>(null);
  const status = useEngineeringInsightsStatus();
  const [range, setRange] = useState<RangeState>(() => ({
    preset: "6m",
    ...rangeForPreset("6m"),
  }));
  const [squadMode, setSquadMode] = useState<SquadMode>("all");
  const [granularity, setGranularity] = useState<EngineeringInsightsGranularity>("month");

  // Meeting Mode deck. The arrangement (ordered slides of 1–3 cards + the
  // "Not presented" tray) lives in a shared server document; the modal edits
  // a draft and saves transactionally, so the page just resolves whatever is
  // stored. Both stay disabled until the settings document loads.
  const settings = useEngineeringPresentationSettings();
  const [presenting, setPresenting] = useState(false);
  const [arranging, setArranging] = useState(false);

  const arrangement = useMemo(
    () =>
      settings.data ? resolveArrangement(ENGINEERING_CHART_SPECS, settings.data) : null,
    [settings.data],
  );
  const deck = useMemo(
    () => (arrangement ? resolveDeck(ENGINEERING_CHART_SPECS, arrangement.slides) : []),
    [arrangement],
  );
  const deckReady = arrangement !== null;

  // The one place the range→granularity auto-rule fires: short ranges flip
  // the dashboard to weekly (one-shot; the user can still override after).
  const applyRange = (next: RangeState) => {
    setRange(next);
    if (autoGranularityForRange(next.from, next.to) === "week") {
      setGranularity("week");
    }
  };

  const sections = useMemo(
    () =>
      ENGINEERING_CHART_SECTIONS.map((section) => ({
        section,
        specs: ENGINEERING_CHART_SPECS.filter((spec) => spec.section === section),
      })).filter((entry) => entry.specs.length > 0),
    [],
  );


  if (status.isLoading) {
    return (
      <div className="page">
        <PageHeader
          eyebrow="Dashboards"
          title="Engineering Insights"
          subtitle="Delivery flow, DORA, quality, and per-engineer metrics for the AIHR squads."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((n) => (
            <Card key={n} className="p-5">
              <div className="h-64 w-full rounded-[10px] bg-muted animate-pulse" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!status.data?.enabled) {
    return (
      <div className="page">
        <PageHeader
          eyebrow="Dashboards"
          title="Engineering Insights"
          subtitle="Delivery flow, DORA, quality, and per-engineer metrics for the AIHR squads."
        />
        <EmptyState
          title="Engineering Insights is not enabled in this environment"
          body="The engineering metrics index has not been configured here. Ask an operator to enable the engineering-insights ingestion for this environment."
          icon={BarChart3}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen gap-0">
      <main className="page min-w-0 flex-1">
        <PageHeader
          eyebrow="Dashboards"
          title="Engineering Insights"
          subtitle="Delivery flow, DORA, quality, and per-engineer metrics for the AIHR squads."
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => setArranging(true)}
                disabled={!deckReady}
                aria-label="Arrange presentation"
                title="Arrange presentation"
              >
                <Presentation className="h-3.5 w-3.5" />
              </Button>
              <Button
                onClick={() => setPresenting(true)}
                disabled={!deckReady || deck.length === 0}
                title={
                  deckReady && deck.length === 0
                    ? "Add at least one card to the presentation"
                    : undefined
                }
              >
                <Play className="h-3.5 w-3.5" />
                Start presentation
                {deckReady
                  ? ` · ${deck.length} ${deck.length === 1 ? "slide" : "slides"}`
                  : ""}
              </Button>
            </div>
          }
        />

        {settings.isError && (
          <div className="mb-4 flex items-center gap-3 rounded-[10px] border border-[var(--danger)]/20 bg-[var(--danger)]/5 px-3 py-2 text-xs text-[var(--danger)]">
            Couldn't load the Meeting Mode deck — presentation is disabled until it loads.
            <Button variant="secondary" size="sm" onClick={() => void settings.refetch()}>
              Retry
            </Button>
          </div>
        )}
        <Card className="ei-toolbar p-4 mb-6 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {RANGE_PRESETS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => applyRange({ preset: option.id, ...rangeForPreset(option.id) })}
                  className={cn(
                    "h-8 px-3 rounded-[10px] text-xs font-medium transition-colors ring-focus",
                    range.preset === option.id
                      ? "bg-[var(--primary)] text-[color:var(--primary-foreground)]"
                      : "text-anchor hover:bg-muted border border-soft",
                  )}
                >
                  {option.label}
                </button>
              ))}
              <DateRangePopover
                from={range.from}
                to={range.to}
                isCustom={range.preset === "custom"}
                onApply={({ from, to }) => applyRange({ preset: "custom", from, to })}
              />
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">Granularity</span>
              {GRANULARITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setGranularity(option.value)}
                  className={cn(
                    "h-8 px-3 rounded-[10px] text-xs font-medium transition-colors ring-focus",
                    granularity === option.value
                      ? "bg-[var(--primary)] text-[color:var(--primary-foreground)]"
                      : "text-anchor hover:bg-muted border border-soft",
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="relative flex h-8 items-center gap-2 rounded-[10px] border border-soft bg-surface pl-2.5 pr-7 cursor-pointer transition-colors hover:bg-muted focus-within:border-[var(--primary)]">
              <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <select
                value={squadMode}
                onChange={(event) => setSquadMode(event.target.value as SquadMode)}
                aria-label="Squad filter"
                className="appearance-none cursor-pointer text-xs font-medium text-anchor outline-none"
              >
                {SQUAD_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 h-3.5 w-3.5 text-muted-foreground" />
            </label>
          </div>
        </Card>

        {sections.map(({ section, specs }) => (
          <section key={section} className="mb-8">
            <h2 className="text-base font-semibold text-anchor mb-3">{section}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {specs.map((spec) => (
                <EngineeringChart
                  key={spec.id}
                  spec={spec}
                  from={range.from}
                  to={range.to}
                  squadMode={squadMode}
                  granularity={granularity}
                  onAsk={(prompt) => chatRef.current?.ask(prompt)}
                />
              ))}
            </div>
          </section>
        ))}
      </main>
      <InsightsChatSidebar ref={chatRef} />
      {arranging && arrangement && (
        <ArrangeDeckModal
          specs={ENGINEERING_CHART_SPECS}
          initial={arrangement}
          updatedAt={settings.data?.updatedAt ?? null}
          updatedBy={settings.data?.updatedBy ?? null}
          onClose={() => setArranging(false)}
        />
      )}
      {presenting && deck.length > 0 && (
        <MeetingModeOverlay
          deck={deck}
          from={range.from}
          to={range.to}
          squadMode={squadMode}
          granularity={granularity}
          rangePreset={range.preset}
          onRangePresetChange={(preset) =>
            applyRange({ preset, ...rangeForPreset(preset) })
          }
          onGranularityChange={setGranularity}
          onSquadModeChange={setSquadMode}
          onClose={() => setPresenting(false)}
        />
      )}
    </div>
  );
}
